import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { AcUnit, AttachMoneyRounded, Edit, Info, LocationOn, Phone, SyncAlt, Description, NoteAlt, Inventory2 } from '@mui/icons-material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { format } from 'date-fns';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { GiCrackedGlass } from 'react-icons/gi';

import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { useSnackBar } from '../../../contexts/snackbar';

import { API_ENDPOINT } from '../../../configurations/global.config';

import { DeliveryState, Workflow } from '../../../enums/orders';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { OrderRoute } from '../../../interfaces/common/routes';

import { display_phone_number } from '../../../utils/phone_numbers';
import { displayLocation } from '../../../utils/locations';
import { displayMoney, minus } from '../../../utils/money';
import { get_cod } from '../../../utils/cod';
import {
  user_has_data_model_access,
  DataResources,
  ResourceActionType,
  user_is_merchant,
} from '../../../utils/permissions';

import { showDeliveryState, showPaymentState } from '../styles/order-states.style';
import { showWorkflow } from '../styles/workflow-display';
import FreezeOrderDialog from '../frozen-orders/freeze-order-dialog';
import UnfreezeOrderDialog from '../frozen-orders/unfreeze-order-dialog';
import CodUpdateDialog from '../cod-update/cod-update-dialog/cod-update-dialog';
import { RouteMap } from './route-map';
import MapStyleSelector from '../map-style-selector/map-style-selector';
import { useMapStyle } from '../../../contexts/map-style-context';
import OrderActionsHeader, { OrderAction, createFreezeAction, createRestoreAction } from './order-actions-header';
import { UserType } from '../../../enums/users';
import { PrintWaybillsMultiSizes } from '../bulk-action/print-waybills';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';

interface OrderDetailsWithMapProps {
  order: OrderType;
  setOrder: React.Dispatch<React.SetStateAction<OrderType | undefined>>;
  additionalPrimaryActions?: OrderAction[];
  additionalSecondaryActions?: OrderAction[];
  canEditBasedOnBranch?: boolean;
}

/** small helpers */
function 
  LabeledValue({
  label,
  icon,
  value,
  dense = false,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  dense?: boolean;
  mono?: boolean;
  icon: React.ReactNode
}) {
  return (
    <Box 
      sx={{ 
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        {icon}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ 
            textTransform: 'uppercase', 
            letterSpacing: 0.8,
            fontWeight: 700,
            fontSize: '0.65rem'
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        variant={dense ? 'body2' : 'body1'}
        sx={{ 
          textAlign: "center", 
          fontFamily: mono ? 'ui-monospace, Menlo, monospace' : undefined,
          fontWeight: 600,
          color: 'text.primary',
          fontSize: '0.95rem'
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SectionCard({
  title,
  subtitle,
  avatar,
  action,
  children,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  avatar?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderColor: 'primary.light',
        }
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.01) 100%)',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {avatar}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3} sx={{ color: 'text.primary' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {action}
      </Box>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>{children}</CardContent>
    </Card>
  );
}

export default function OrderDetailsWithMap({ 
  order, 
  setOrder,
  additionalPrimaryActions = [],
  additionalSecondaryActions = [],
  canEditBasedOnBranch = true,
}: OrderDetailsWithMapProps) {
  const navigate = useNavigate();
  const { color } = useContext(OrgSettingsContext);
  const { user, config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const canUpdateOrder = user?.type == UserType.MERCHANT || user_has_data_model_access(
    user,
    DataResources.ORDER,
    ResourceActionType.UPDATE
  );

  const [isEditingCod, setIsEditingCod] = useState(false);
  const [requestingFreezeUpdate, setRequestingFreezeUpdate] = useState<
    'freeze' | 'unfreeze' | undefined
  >();
  const [orderRoute, setOrderOrderRoute] = useState<OrderRoute>();
  const [loadingRoute, setLoadingRoute] = useState<boolean>(true);
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  // Map style from context
  const { mapStyle } = useMapStyle();

  const isReturnWorkflow = order.workflow === Workflow.RETURN;
  const cod = useMemo(() => get_cod(order), [order]);
  const merchantDue = isReturnWorkflow ? cod : minus(order.price, order.pop);

  /** load route snapshot */
  useEffect(() => {
    const loadRoute = async () => {
      setLoadingRoute(true);
      try {
        const { data } = await axios.get<OrderRoute>(
          `${API_ENDPOINT}/v1/order/${order.id}/route/`,
          config
        );
        setOrderOrderRoute(data);
      } catch {
        setOrderOrderRoute(undefined);
        showMessage('Something went wrong. Try refreshing the page', 'error');
      } finally {
        setLoadingRoute(false);
      }
    };
    loadRoute();
  }, [order.id]);

  /** Restore order handler */
  const handleRestoreOrder = async () => {
    try {
      const { data } = await axios.post<OrderType[]>(
        `${API_ENDPOINT}/v1/orders/restore/`,
        { order_ids: [order.id] },
        config
      );
      setOrder({ ...order, delivery_state: data[0].delivery_state });
      showMessage('Order restored successfully', 'success');
    } catch (error) {
      showMessage('Failed to restore order', 'error');
    }
  };

  /** Build actions for the header */
  const buildActions = () => {
    const primary: OrderAction[] = [];

    // Priority order: Edit Order > Cancel Order > Print > Freeze/Unfreeze/Restore
    
    // 1. Add any additional primary actions first (Edit Order, Cancel Order, etc.)
    primary.push(...additionalPrimaryActions);
    
    // 2. Add any additional secondary actions (they become primary now)
    primary.push(...additionalSecondaryActions);
    
    // 3. Print action
    const printAction: OrderAction = {
      key: 'print',
      label: 'Print',
      icon: <PrintIcon />,
      onClick: () => setShowPrintDialog(true),
      color: 'primary',
      variant: 'outlined',
    };
    primary.push(printAction);

    // 4. Freeze/Unfreeze/Restore actions (lowest priority)
    if (canEditBasedOnBranch) {
      // If order is cancelled, show restore action
      if (order.delivery_state === DeliveryState.CANCELLED) {
        primary.push(createRestoreAction(handleRestoreOrder));
      } else {
        // Otherwise show freeze/unfreeze action
        const freezeAction = createFreezeAction(
          order.is_frozen ?? false,
          canUpdateOrder,
          order.delivery_state,
          () => setRequestingFreezeUpdate('freeze'),
          () => setRequestingFreezeUpdate('unfreeze')
        );
        primary.push(freezeAction);
      }
    }

    return { primary, secondary: [] };
  };

  const { primary: allPrimaryActions } = buildActions();

  /** HEADER (Order summary with actions) */
  const header = (
    <Stack spacing={1.5}>
      {/* Main Order Info Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="h5" fontWeight={700} sx={{ color: 'primary.main' }}>
                  Order #{order.order_id}
                </Typography>
                {order.reference_id && (
                  <Chip 
                    size="small" 
                    variant="filled" 
                    label={`Ref: ${order.reference_id}`}
                    sx={{ bgcolor: 'background.paper', fontWeight: 600 }}
                  />
                )}
                {/* Number of Packages */}
                <Chip
                  icon={<Inventory2 fontSize="small" />}
                  label={`${order.number_of_packages} ${order.number_of_packages === 1 ? 'Package' : 'Packages'}`}
                  size="small"
                  color="default"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              
              {/* Workflow Direction Indicator */}
              {showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason)}
            </Stack>

            {/* Actions in Header */}
            {allPrimaryActions.length > 0 && (
              <Box>
                <OrderActionsHeader
                  primaryActions={allPrimaryActions}
                  secondaryActions={[]}
                />
              </Box>
            )}
          </Stack>
        </Box>

        {/* Status, Properties, Description and Notes Section */}
        <Box sx={{ px: 2.5, py: 2, bgcolor: 'background.paper' }}>
          <Stack spacing={2} divider={<Divider />}>
            {/* Status and Properties */}
            <Grid2 container spacing={2}>
              <Grid2 xs={12} md={(order.is_critical || order.is_exchange || order.is_fragile || (order.is_frozen && order.frozen_until)) ? 6 : 12}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                    Status
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {showDeliveryState(order.delivery_state)}
                    {showPaymentState(order.payment_state)}
                  </Box>
                </Box>
              </Grid2>

              {/* Property Chips */}
              {(order.is_critical || order.is_exchange || order.is_fragile || (order.is_frozen && order.frozen_until)) && (
                <Grid2 xs={12} md={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                      Properties
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {order.is_critical && (
                        <Chip
                          icon={<Info fontSize="small" />}
                          color="warning"
                          variant="outlined"
                          label="Critical"
                          size="small"
                        />
                      )}
                      {order.is_exchange && (
                        <Chip 
                          icon={<SyncAlt fontSize="small" />} 
                          color="primary" 
                          variant="outlined" 
                          label="With Exchange"
                          size="small"
                        />
                      )}
                      {order.is_fragile && (
                        <Chip
                          icon={<GiCrackedGlass size={14} />}
                          color="warning"
                          variant="outlined"
                          label="Fragile"
                          size="small"
                        />
                      )}
                      {order.is_frozen && order.frozen_until && (
                        <Chip
                          icon={<AcUnit fontSize="small" />}
                          color="default"
                          variant="outlined"
                          label={`Frozen until ${format(order.frozen_until, 'dd-MM-yyyy')}`}
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid2>
              )}

              {/* Exchange Info */}
              {order.exchange_for && (
                <Grid2 xs={12}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                      Exchange Info
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Exchange for{' '}
                      <a
                        href={`/order/${order.exchange_for}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'underline', color: 'inherit', fontWeight: 600 }}
                      >
                        Order #{order.exchange_for}
                      </a>
                    </Typography>
                  </Box>
                </Grid2>
              )}
            </Grid2>

            {/* Package Details or Description and Notes */}
            {(() => {
              // Check if we have any package descriptions
              const hasPackageDetails = order.packages_metadata && 
                order.packages_metadata.length > 0 && 
                order.packages_metadata.some(pkg => pkg.description && pkg.description.trim() !== '');
              
              // Show package details or description/note
              const showDescription = !hasPackageDetails && order.description;
              const showNote = order.public_note;
              
              if (!hasPackageDetails && !showDescription && !showNote) return null;
              
              return (
                <Grid2 container spacing={2}>
                  {/* Package Details (replaces description) */}
                  {hasPackageDetails && (
                    <Grid2 xs={12} md={showNote ? 6 : 12}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                          <Inventory2 fontSize="small" color="primary" />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Package Details
                          </Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ pl: 3 }}>
                          {order.packages_metadata?.map((pkg, index) => {
                            const hasDescription = pkg.description && pkg.description.trim() !== '';
                            return (
                              <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, minWidth: '80px' }}>
                                  Package {index + 1}:
                                </Typography>
                                <Typography 
                                  variant="body2"
                                  color={hasDescription ? 'text.primary' : 'text.disabled'}
                                  sx={{ fontStyle: hasDescription ? 'normal' : 'italic' }}
                                >
                                  {hasDescription ? pkg.description : 'No description'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grid2>
                  )}
                  
                  {/* Order Description (fallback when no package details) */}
                  {showDescription && (
                    <Grid2 xs={12} md={showNote ? 6 : 12}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                          <Description fontSize="small" color="primary" />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Description
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ pl: 3 }}>
                          {order.description}
                        </Typography>
                      </Box>
                    </Grid2>
                  )}
                  
                  {/* Public Note */}
                  {showNote && (
                    <Grid2 xs={12} md={(hasPackageDetails || showDescription) ? 6 : 12}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                          <NoteAlt fontSize="small" color="primary" />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Note
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ pl: 3 }}>
                          {order.public_note}
                        </Typography>
                      </Box>
                    </Grid2>
                  )}
                </Grid2>
              );
            })()}

            {/* Private Note - Full Width */}
            {order.private_note && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <NoteAlt fontSize="small" color="warning" />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Private Note
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ pl: 3 }}>
                  {order.private_note}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Card>

    </Stack>
  );

  /** MERCHANT CARD */
  const merchantCard = (
    <SectionCard
      title="Merchant"
      subtitle={order.merchant?.name || '—'}
      avatar={
        order.merchant?.logo_url ? (
          <Avatar src={order.merchant.logo_url} alt={order.merchant.name} sx={{ width: 40, height: 40 }} />
        ) : (
          <Avatar sx={{ bgcolor: `${color}22`, color: 'text.primary' }}>M</Avatar>
        )
      }
      action={
        order.merchant?.id ? (
          <Button size="small" onClick={() => navigate(`/merchant/${order.merchant?.id}`)}>
            View
          </Button>
        ) : null
      }
    >
      <Stack spacing={2.5}>
        <Grid2 container spacing={2}>
          <Grid2 xs={12} sm={6}>
            <LabeledValue
              label="Phone"
              icon={<Phone fontSize='small' color="primary" />}
              value={display_phone_number(order.merchant?.phone_number ?? "") || '—'}
            />
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <LabeledValue
              label="Location"
              icon={<LocationOn fontSize='small' color="primary" />}
              value={
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {order.merchant_location?.name || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {displayLocation(order.merchant_location)}
                  </Typography>
                </Box>
              }
            />
          </Grid2>
        </Grid2>
        
        <Divider />
        
        <Grid2 container spacing={2}>
          <Grid2 xs={6}>
            <LabeledValue
              icon={<AttachMoneyRounded fontSize='small' color="success" />}
              label={isReturnWorkflow ? 'Return COD' : 'Price Due'}
              value={<Typography fontWeight={700} color="success.main">{displayMoney(merchantDue)}</Typography>}
            />
          </Grid2>
          {!isReturnWorkflow && (
            <Grid2 xs={6}>
              <LabeledValue 
                icon={<AttachMoneyRounded fontSize='small' color="primary" />}
                label="POP" 
                value={displayMoney(order.pop)} 
              />
            </Grid2>
          )}
        </Grid2>
      </Stack>
    </SectionCard>
  );

  /** CUSTOMER CARD */
  const customerCard = (
    <SectionCard
      title="Customer"
      subtitle={order.customer?.name || '—'}
      avatar={<Avatar sx={{ bgcolor: `${color}22`, color: 'text.primary' }}>C</Avatar>}
      action={
        order.customer?.id ? (
          <Button size="small" onClick={() => navigate(`/customer/${order.customer?.id}`)}>
            View
          </Button>
        ) : null
      }
    >
      <Stack spacing={2.5}>
        <Grid2 container spacing={2}>
          <Grid2 xs={12} sm={6}>
            <LabeledValue
              icon={<Phone fontSize='small' color="primary" />}
              label="Phone"
              value={
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {display_phone_number(order.customer?.phone_number ?? "") || '—'}
                  </Typography>
                  {order.customer?.secondary_phone_number && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {display_phone_number(order.customer.secondary_phone_number)}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Grid2>
          <Grid2 xs={12} sm={6}>
            <LabeledValue
              label="Location"
              icon={<LocationOn fontSize='small' color="primary" />}
              value={
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {order.customer?.name || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {displayLocation(order.customer_location)}
                  </Typography>
                </Box>
              }
            />
          </Grid2>
        </Grid2>

        <Divider />

        {order.workflow === Workflow.STANDARD ? (
          <Grid2 container spacing={2}>
            <Grid2 xs={12}>
              <LabeledValue
                label='COD'
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                    <Typography fontWeight={700} color="success.main">{displayMoney(get_cod(order))}</Typography>
                    {user_is_merchant(user) && (
                      <Tooltip title="Edit COD">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setIsEditingCod(true)}
                            disabled={
                              [DeliveryState.DELIVERED, DeliveryState.CANCELLED, DeliveryState.AWAITING_RETURN, DeliveryState.DELIVERED_AT_WAREHOUSE].includes(
                                order.delivery_state
                              ) || order.workflow !== Workflow.STANDARD
                            }
                            sx={{ ml: 0.5, p: 0.5 }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                }
                icon={<AttachMoneyRounded fontSize='small' color='success' />}
              />
            </Grid2>
          </Grid2>
        ) : (
          <Grid2 container spacing={2}>
            <Grid2 xs={12}>
              <LabeledValue
                icon={<AttachMoneyRounded fontSize='small' color="primary" />}
                label="POP"
                value={<Typography fontWeight={700}>{displayMoney(order.pop)}</Typography>}
              />
            </Grid2>
          </Grid2>
        )}
      </Stack>
    </SectionCard>
  );

  /** ROUTE CARD (Map + quick facts) */
  const routeCard = (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 600 }}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0.02) 100%)',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Route
        </Typography>
        <Stack direction="row" spacing={1}>
          {loadingRoute ? (
            <CircularProgress size={18} />
          ) : orderRoute ? (
            <>
              <Chip
                size="small"
                variant="outlined"
                label={`${(orderRoute.legs?.length ?? 0) + 1} branches`}
              />
              {typeof orderRoute.total_minutes === 'number' && (
                <Chip size="small" variant="outlined" label={`${orderRoute.total_minutes} min`} />
              )}
            </>
          ) : (
            <Chip size="small" color="warning" variant="outlined" label="No snapshot" />
          )}
        </Stack>
      </Box>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {loadingRoute ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight={500}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MapStyleSelector sx={{ mb: 1, alignSelf: 'flex-start' }} />
            <RouteMap order={order} orderRoute={orderRoute} height={580} width={750} mapStyle={mapStyle} />
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Grid2 container spacing={1.5}>
        {/* LEFT: header (with actions), merchant & customer */}
        <Grid2 xs={12} md={5}>
          <Stack spacing={1.5}>
            {header}
            {merchantCard}
            {customerCard}
          </Stack>
        </Grid2>

        {/* RIGHT: route */}
        <Grid2 xs={12} md={7}>
          {routeCard}
        </Grid2>
      </Grid2>

      {/* Dialogs */}
      {requestingFreezeUpdate === 'freeze' && (
        <FreezeOrderDialog
          onClose={() => setRequestingFreezeUpdate(undefined)}
          order={order}
          setOrder={(o) => setOrder(o)}
        />
      )}

      {requestingFreezeUpdate === 'unfreeze' && (
        <UnfreezeOrderDialog
          onClose={() => setRequestingFreezeUpdate(undefined)}
          orders={[order]}
          onSuccess={() => {
            setOrder({
              ...order,
              frozen_because: undefined,
              frozen_until: undefined,
              is_frozen: false,
            });
          }}
        />
      )}

      {isEditingCod && (
        <CodUpdateDialog
          order={order}
          open={isEditingCod}
          onClose={() => setIsEditingCod(false)}
          onSuccess={(newCod) => {
            const delivery_fee = order.delivery_fee;
            setOrder({
              ...order,
              // keep price = COD - delivery_fee (your current convention)
              price: minus(newCod, delivery_fee),
            });
            showMessage('COD updated', 'success');
            setIsEditingCod(false);
          }}
        />
      )}

      {/* Print Dialog */}
      <Dialog
        open={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Print Waybill</Typography>
            <IconButton onClick={() => setShowPrintDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <PrintWaybillsMultiSizes 
            orders={[order]} 
            onClose={() => setShowPrintDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
